// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';

import { installer, InstallerDO } from '../../../requests';

interface Props {
  rowId: number;
  notify?: () => void;
}

interface State {
  loading?: boolean;
  data?: Partial<InstallerDO>;
}

export default class Edit extends React.Component <Props, State> {
  private pid: string;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;
    this.pid = window.getStr();

    this.state = {
      loading: false,
      data: {
        active: false,
      }
    };

    this.defaultValues = {};
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};
    const isNew: boolean = !!isNaN(rowId);

    this.loadingStart();
    try {
      let rowData: Partial<InstallerDO> = this.state.data || {};
      if (!isNew) {
        rowData = await installer<Partial<InstallerDO>>('get', String(rowId)) || {};
      }
      this.setState(
        {
          data: { ...rowData, },
        },
        () => {
          this.defaultValues = _.cloneDeep(rowData);
        }
      );
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = (event: React.FormEvent<HTMLFormElement>): any => {
    event.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    installer('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Installer successfully ${data?.id ? 'edited' : 'created'} ${data?.name}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('IE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, type, checked } = event.target;
    const value: any = (type === 'checkbox' ? checked : event.target.value);
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        [name]: value,
      },
    }));
  };

  public render(): JSX.Element {
    const { data } = this.state;

    return (
      <div id="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={this.state.loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          User {data?.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form formValues={this.state} onChange={this.handleChange} />
                      <ButtonsForm formValues={this.state} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
